import type { AxiosInstance } from "axios";

export type ExportLogType = "video" | "audio" | "transcript";
export type ExportLogStatus = "started" | "completed" | "failed" | "cancelled";

type ExportLoggerContext = {
  axios: AxiosInstance | null | undefined;
  getToken: (() => string | null) | null | undefined;
};

export type CreateExportLogPayload = {
  projectId?: string;
  compositionId?: string;
  compositionTitle?: string;
  type: ExportLogType;
  filename?: string;
  format?: string;
  quality?: string;
  width?: number;
  height?: number;
  durationSec?: number;
};

export type UpdateExportLogPayload = {
  logId: string | null;
  status: Exclude<ExportLogStatus, "started">;
  filename?: string;
  format?: string;
  quality?: string;
  width?: number;
  height?: number;
  durationSec?: number;
  error?: string;
};

export async function createExportLog(
  ctx: ExportLoggerContext,
  payload: CreateExportLogPayload,
): Promise<string | null> {
  const axios = ctx.axios ?? null;
  const token = ctx.getToken?.() ?? null;
  if (!axios || !token) return null;

  try {
    const response = await axios.post(
      "/export/log",
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    const logId = String(response?.data?.logId ?? "").trim();
    return logId || null;
  } catch {
    return null;
  }
}

export async function updateExportLog(
  ctx: ExportLoggerContext,
  payload: UpdateExportLogPayload,
): Promise<void> {
  const axios = ctx.axios ?? null;
  const token = ctx.getToken?.() ?? null;
  const logId = String(payload.logId ?? "").trim();
  if (!axios || !token || !logId) return;

  try {
    await axios.post(
      `/export/log/${encodeURIComponent(logId)}`,
      {
        status: payload.status,
        filename: payload.filename,
        format: payload.format,
        quality: payload.quality,
        width: payload.width,
        height: payload.height,
        durationSec: payload.durationSec,
        error: payload.error,
      },
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
  } catch {
    // Best-effort only.
  }
}
